import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Shield, FileText, CheckCircle2, XCircle,
  Clock, User, Building2, CreditCard, AlertTriangle, Loader2, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoFull } from '@/components/Logo';
import { api } from '@/services/api';
import { toast } from 'sonner';

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

const STATUS_CONFIG = {
  not_started:          { label: 'Not Started',      color: '#8DA0B9', icon: Clock },
  pending:              { label: 'Info Requested',    color: '#F59E0B', icon: AlertTriangle },
  documents_submitted:  { label: 'Submitted',         color: '#00AEEF', icon: FileText },
  in_review:            { label: 'In Review',         color: '#3D5A80', icon: Loader2 },
  approved:             { label: 'Approved',           color: '#83CF44', icon: CheckCircle2 },
  denied:               { label: 'Denied',             color: '#EF4444', icon: XCircle },
  pending_configuration:{ label: 'Legacy',             color: '#8DA0B9', icon: Clock }
};

const CheckBadge = ({ check }) => {
  if (!check || typeof check === 'string') return <span className="text-xs text-[#8DA0B9]">Pending</span>;
  const s = check.status;
  const color = s === 'passed' || s === 'clear' ? '#83CF44' : s === 'failed' ? '#EF4444' : '#F59E0B';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-xs font-medium capitalize" style={{ color }}>{s}</span>
      {check.score && <span className="text-[10px] text-[#8DA0B9]">({check.score}%)</span>}
    </div>
  );
};

const KycVerification = () => {
  const [step, setStep] = useState(0); // 0=status, 1=personal, 2=business, 3=document, 4=review
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '', date_of_birth: '', ssn_last4: '',
    street: '', city: '', state: 'CA', zip_code: '',
    business_name: '', ein: '', business_type: 'llc', state_of_incorporation: 'CA',
    document_type: 'drivers_license', document_number: '', document_expiry: ''
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getMyKyc();
        setVerification(data);
        if (data.status === 'not_started') setStep(1);
      } catch { setStep(1); }
      finally { setLoading(false); }
    })();
  }, []);

  const updateForm = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await api.submitKyc(form);
      setVerification(result);
      setStep(0);
      toast.success('KYC verification submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const canProceed = (s) => {
    if (s === 1) return form.full_name && form.date_of_birth && form.street && form.city && form.state && form.zip_code;
    if (s === 2) return form.business_name && form.ein;
    if (s === 3) return form.document_type && form.document_number;
    return true;
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
    </div>
  );

  const cfg = STATUS_CONFIG[verification?.status] || STATUS_CONFIG.not_started;
  const StatusIcon = cfg.icon;
  const showForm = step >= 1 && (!verification || verification.status === 'not_started' || verification.status === 'pending' || verification.status === 'denied');

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="kyc-page">
      <header className="bg-white border-b border-[#DCE4ED] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/builder" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors" data-testid="back-to-builder">
              <ArrowLeft size={18} /><span className="text-sm">Back</span>
            </Link>
            <div className="w-px h-6 bg-[#DCE4ED]" />
            <h1 className="text-lg font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>KYC/AML Verification</h1>
          </div>
          <LogoFull size="small" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* STATUS VIEW */}
        {(step === 0 && verification && verification.status !== 'not_started') && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-xl border border-[#DCE4ED] p-6" data-testid="kyc-status-card">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cfg.color}15` }}>
                  <StatusIcon size={24} style={{ color: cfg.color }} className={verification.status === 'in_review' ? 'animate-spin' : ''} />
                </div>
                <div>
                  <p className="text-sm text-[#8DA0B9]">Verification Status</p>
                  <p className="text-xl font-bold" style={{ color: cfg.color }}>{cfg.label}</p>
                </div>
              </div>

              {verification.risk_level && (
                <div className="mb-6 p-3 rounded-lg bg-[#F0F4F8]">
                  <span className="text-xs text-[#8DA0B9]">Risk Level: </span>
                  <span className={`text-sm font-bold capitalize ${verification.risk_level === 'low' ? 'text-green-600' : verification.risk_level === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>
                    {verification.risk_level}
                  </span>
                </div>
              )}

              {/* Checks */}
              {verification.checks && typeof verification.checks === 'object' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Object.entries(verification.checks).map(([k, v]) => (
                    <div key={k} className="p-3 bg-[#F0F4F8] rounded-lg" data-testid={`kyc-check-${k}`}>
                      <p className="text-xs text-[#8DA0B9] mb-1 capitalize">{k.replace(/_/g, ' ')}</p>
                      <CheckBadge check={v} />
                      {v?.details && <p className="text-[10px] text-[#8DA0B9] mt-1 line-clamp-2">{v.details}</p>}
                    </div>
                  ))}
                </div>
              )}

              {verification.reviewer_notes && (
                <div className="mt-4 p-3 bg-[#FFF8E1] rounded-lg border border-[#FFE082]">
                  <p className="text-xs font-medium text-[#F59E0B] mb-1">Reviewer Notes</p>
                  <p className="text-sm text-[#0D1B2A]">{verification.reviewer_notes}</p>
                </div>
              )}

              {verification.denial_reason && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs font-medium text-red-500 mb-1">Denial Reason</p>
                  <p className="text-sm text-[#0D1B2A]">{verification.denial_reason}</p>
                </div>
              )}
            </div>

            {/* Timeline */}
            {verification.timeline?.length > 0 && (
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6" data-testid="kyc-timeline">
                <h3 className="text-sm font-semibold text-[#0D1B2A] mb-4">Timeline</h3>
                <div className="space-y-3">
                  {verification.timeline.map((t, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#00AEEF] mt-1.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-[#0D1B2A]">{t.action}</p>
                        <p className="text-xs text-[#8DA0B9]">{new Date(t.at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(verification.status === 'denied' || verification.status === 'pending') && (
              <Button onClick={() => setStep(1)} className="bg-[#1A3A64] hover:bg-[#0D1B2A] text-white" data-testid="kyc-resubmit-btn">
                {verification.status === 'denied' ? 'Resubmit Verification' : 'Update Information'}
              </Button>
            )}
          </motion.div>
        )}

        {/* FORM WIZARD */}
        <AnimatePresence mode="wait">
          {showForm && (
            <motion.div key={`step-${step}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              {/* Progress */}
              <div className="flex items-center gap-2 mb-8">
                {['Personal', 'Business', 'Document', 'Review'].map((lbl, i) => (
                  <React.Fragment key={i}>
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${step === i + 1 ? 'bg-[#1A3A64] text-white' : step > i + 1 ? 'bg-[#00AEEF]/10 text-[#00AEEF]' : 'bg-[#E8EEF4] text-[#8DA0B9]'}`}>
                      {step > i + 1 ? <CheckCircle2 size={12} /> : <span>{i + 1}</span>}
                      <span className="hidden sm:inline">{lbl}</span>
                    </div>
                    {i < 3 && <div className={`flex-1 h-0.5 ${step > i + 1 ? 'bg-[#00AEEF]' : 'bg-[#E8EEF4]'}`} />}
                  </React.Fragment>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                {step === 1 && (
                  <div className="space-y-5" data-testid="kyc-step-personal">
                    <div className="flex items-center gap-3 mb-6">
                      <User size={20} className="text-[#1A3A64]" />
                      <h2 className="text-base font-semibold text-[#0D1B2A]">Personal Information</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#8DA0B9] mb-1 block">Full Legal Name *</label>
                        <Input value={form.full_name} onChange={e => updateForm('full_name', e.target.value)} placeholder="John Smith" data-testid="kyc-full-name" />
                      </div>
                      <div>
                        <label className="text-xs text-[#8DA0B9] mb-1 block">Date of Birth *</label>
                        <Input type="date" value={form.date_of_birth} onChange={e => updateForm('date_of_birth', e.target.value)} data-testid="kyc-dob" />
                      </div>
                      <div>
                        <label className="text-xs text-[#8DA0B9] mb-1 block">SSN (Last 4 digits)</label>
                        <Input value={form.ssn_last4} onChange={e => updateForm('ssn_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="1234" maxLength={4} data-testid="kyc-ssn" />
                      </div>
                    </div>
                    <div className="border-t border-[#DCE4ED] pt-4 mt-4">
                      <p className="text-xs text-[#8DA0B9] mb-3">Residential Address</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <Input value={form.street} onChange={e => updateForm('street', e.target.value)} placeholder="Street address *" data-testid="kyc-street" />
                        </div>
                        <Input value={form.city} onChange={e => updateForm('city', e.target.value)} placeholder="City *" data-testid="kyc-city" />
                        <div className="grid grid-cols-2 gap-3">
                          <div className="relative">
                            <select value={form.state} onChange={e => updateForm('state', e.target.value)} className="w-full border border-[#DCE4ED] rounded-lg px-3 py-2 text-sm appearance-none bg-white" data-testid="kyc-state">
                              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8DA0B9] pointer-events-none" />
                          </div>
                          <Input value={form.zip_code} onChange={e => updateForm('zip_code', e.target.value)} placeholder="ZIP *" data-testid="kyc-zip" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5" data-testid="kyc-step-business">
                    <div className="flex items-center gap-3 mb-6">
                      <Building2 size={20} className="text-[#1A3A64]" />
                      <h2 className="text-base font-semibold text-[#0D1B2A]">Business Information</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#8DA0B9] mb-1 block">Business Name *</label>
                        <Input value={form.business_name} onChange={e => updateForm('business_name', e.target.value)} placeholder="Acme Corp" data-testid="kyc-biz-name" />
                      </div>
                      <div>
                        <label className="text-xs text-[#8DA0B9] mb-1 block">EIN (Tax ID) *</label>
                        <Input value={form.ein} onChange={e => updateForm('ein', e.target.value)} placeholder="12-3456789" data-testid="kyc-ein" />
                      </div>
                      <div className="relative">
                        <label className="text-xs text-[#8DA0B9] mb-1 block">Business Type</label>
                        <select value={form.business_type} onChange={e => updateForm('business_type', e.target.value)} className="w-full border border-[#DCE4ED] rounded-lg px-3 py-2 text-sm appearance-none bg-white" data-testid="kyc-biz-type">
                          <option value="llc">LLC</option>
                          <option value="corporation">Corporation</option>
                          <option value="partnership">Partnership</option>
                          <option value="sole_proprietor">Sole Proprietor</option>
                          <option value="nonprofit">Nonprofit</option>
                        </select>
                        <ChevronDown size={14} className="absolute right-2 top-9 text-[#8DA0B9] pointer-events-none" />
                      </div>
                      <div className="relative">
                        <label className="text-xs text-[#8DA0B9] mb-1 block">State of Incorporation</label>
                        <select value={form.state_of_incorporation} onChange={e => updateForm('state_of_incorporation', e.target.value)} className="w-full border border-[#DCE4ED] rounded-lg px-3 py-2 text-sm appearance-none bg-white" data-testid="kyc-inc-state">
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <ChevronDown size={14} className="absolute right-2 top-9 text-[#8DA0B9] pointer-events-none" />
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-5" data-testid="kyc-step-document">
                    <div className="flex items-center gap-3 mb-6">
                      <CreditCard size={20} className="text-[#1A3A64]" />
                      <h2 className="text-base font-semibold text-[#0D1B2A]">Identity Document</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="relative">
                        <label className="text-xs text-[#8DA0B9] mb-1 block">Document Type *</label>
                        <select value={form.document_type} onChange={e => updateForm('document_type', e.target.value)} className="w-full border border-[#DCE4ED] rounded-lg px-3 py-2 text-sm appearance-none bg-white" data-testid="kyc-doc-type">
                          <option value="drivers_license">Driver's License</option>
                          <option value="passport">Passport</option>
                          <option value="state_id">State ID</option>
                          <option value="ein_letter">EIN Confirmation Letter</option>
                          <option value="articles_of_incorporation">Articles of Incorporation</option>
                          <option value="business_license">Business License</option>
                        </select>
                        <ChevronDown size={14} className="absolute right-2 top-9 text-[#8DA0B9] pointer-events-none" />
                      </div>
                      <div>
                        <label className="text-xs text-[#8DA0B9] mb-1 block">Document Number *</label>
                        <Input value={form.document_number} onChange={e => updateForm('document_number', e.target.value)} placeholder="D1234567" data-testid="kyc-doc-number" />
                      </div>
                      <div>
                        <label className="text-xs text-[#8DA0B9] mb-1 block">Expiration Date</label>
                        <Input type="date" value={form.document_expiry} onChange={e => updateForm('document_expiry', e.target.value)} data-testid="kyc-doc-expiry" />
                      </div>
                    </div>
                    <div className="p-4 bg-[#F0F4F8] rounded-lg border border-dashed border-[#DCE4ED] text-center">
                      <FileText size={24} className="mx-auto text-[#8DA0B9] mb-2" />
                      <p className="text-sm text-[#3D5A80]">Document upload simulated</p>
                      <p className="text-xs text-[#8DA0B9]">In production, documents would be uploaded to a secure vault</p>
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-5" data-testid="kyc-step-review">
                    <div className="flex items-center gap-3 mb-6">
                      <Shield size={20} className="text-[#1A3A64]" />
                      <h2 className="text-base font-semibold text-[#0D1B2A]">Review & Submit</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <h3 className="text-xs font-medium text-[#8DA0B9] uppercase tracking-wider">Personal</h3>
                        <div className="text-sm text-[#0D1B2A]">
                          <p className="font-medium">{form.full_name}</p>
                          <p>{form.date_of_birth}</p>
                          <p>{form.street}, {form.city}, {form.state} {form.zip_code}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-xs font-medium text-[#8DA0B9] uppercase tracking-wider">Business</h3>
                        <div className="text-sm text-[#0D1B2A]">
                          <p className="font-medium">{form.business_name}</p>
                          <p>EIN: {form.ein}</p>
                          <p className="capitalize">{form.business_type} - {form.state_of_incorporation}</p>
                        </div>
                      </div>
                      <div className="space-y-3 sm:col-span-2">
                        <h3 className="text-xs font-medium text-[#8DA0B9] uppercase tracking-wider">Document</h3>
                        <div className="text-sm text-[#0D1B2A]">
                          <p className="capitalize">{form.document_type.replace(/_/g, ' ')} - {form.document_number}</p>
                          {form.document_expiry && <p>Expires: {form.document_expiry}</p>}
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-[#F0F4F8] rounded-lg text-xs text-[#8DA0B9]">
                      By submitting, you authorize InsureLab to verify your identity and business information through our verification partners for compliance with US KYC/AML regulations.
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between mt-8 pt-4 border-t border-[#DCE4ED]">
                  <Button variant="outline" onClick={() => setStep(s => Math.max(s - 1, verification?.status === 'not_started' ? 1 : 0))} disabled={step <= 1} data-testid="kyc-back-btn">
                    <ArrowLeft size={16} className="mr-1" /> Back
                  </Button>
                  {step < 4 ? (
                    <Button onClick={() => setStep(s => s + 1)} disabled={!canProceed(step)} className="bg-[#1A3A64] hover:bg-[#0D1B2A] text-white" data-testid="kyc-next-btn">
                      Next <ArrowRight size={16} className="ml-1" />
                    </Button>
                  ) : (
                    <Button onClick={handleSubmit} disabled={submitting} className="bg-[#00AEEF] hover:bg-[#00AEEF]/90 text-white" data-testid="kyc-submit-btn">
                      {submitting ? <><Loader2 size={16} className="mr-1 animate-spin" /> Submitting...</> : <><Shield size={16} className="mr-1" /> Submit Verification</>}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default KycVerification;
